<template>
  <div>
    <VMenu
      ref="listMenuRef"
      v-model="showLists"
      data-testid="listMenu"
      position="bottom"
      retain-focus
      :close-on-content-click="false"
      :max-height="menuHeight"
      :min-width="menuWidth"
      :max-width="menuWidth"
      :content-class="contentClass"
      left
      :attach="attach"
    >
      <ListMenu
        :loading="loading"
        :product="product"
        :is-attached="isAttached"
        @mouseleave="showLists=false"
        @close="showLists=false"
        @create-list="newWishlist=true"
        @updating="emits('updating')"
        @click.stop.prevent
      />
      <template #activator="{ props: listProps }">
        <!-- Only onClick is taken from listProps: its onKeydown opens the menu on ArrowDown, skipping the auth check in openLists. -->
        <SBtn
          ref="wishlistButtonRef"
          :text="textButton"
          :height="height"
          min-width="32px"
          elevation="0"
          class="font-weight-medium pa-0"
          :class="{
            'selected': filled,
            'wishlist-button': !text,
            'wishlist-text-button': !!text
          }"
          :aria-expanded="showLists || newWishlist"
          :aria-label="text ? undefined : $t('product.wishlist.aria-label')"
          data-testid="wishlistButton"
          @click.stop.prevent="openLists($event, listProps.onClick)"
        >
          <HeartIcon :filled="filled" size="20" :label-aria="$t('product.wishlist.aria-label')" :interactive="false" />
          <span v-if="text" class="pl-2">{{ text }}</span>
        </SBtn>
      </template>
    </VMenu>
    <!-- Positioned against the heart via `target`, not `activator`: an activator would also bind VMenu's click/ArrowDown open handlers to the heart. -->
    <VMenu
      ref="newWishlistMenuRef"
      v-model="newWishlist"
      data-testid="newWishlist"
      retain-focus
      :close-on-content-click="false"
      :content-class="contentClass"
      position="bottom"
      :attach="attach"
      :target="wishlistButtonEl"
      :min-width="menuWidth"
      :max-width="menuWidth"
    >
      <WishlistEdit
        @cancel="closeNewWishlist"
        @created="closeNewWishlist"
        @click.stop.prevent
        @close="closeMenu"
      />
    </VMenu>
  </div>
</template>

<script lang="ts" setup>
import type { ListedProduct as Product } from '~/types/shop'
import HeartIcon from '~/components/base/HeartIcon.vue'
import ListMenu from '~/components/account/lists/ListMenu.vue'
import WishlistEdit from '~/components/account/lists/WishlistEdit.vue'
import { useAuthStore } from '~/stores/auth'
import { useWishlistStore } from '~/stores/wishlist'
import { useRootStore } from '~/stores/root'
import { useBreakpoints } from "~/composables/display";
import { useWishlist } from "~/composables/roles";

interface Props {
  textButton?: boolean
  categoryPage?: boolean
  product: Product
  noBorder?: boolean
  text?: string
  height?: number
}

const props = withDefaults(defineProps<Props>(), {
  textButton: false,
  categoryPage: false,
  noBorder: false,
  text: undefined,
  height: 30
})

const emits = defineEmits<{
  (e: 'updating'): void
}>()

const FOCUSABLE = 'button:not([disabled]), [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'

const showLists = ref(false)
const newWishlist = ref(false)
const isMounted = ref(false)
const wishlistButtonRef = useTemplateRef('wishlistButtonRef')
const listMenuRef = useTemplateRef('listMenuRef')
const newWishlistMenuRef = useTemplateRef('newWishlistMenuRef')

const wishlistButtonEl = computed(() => wishlistButtonRef.value?.$el)

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const wishlistStore = useWishlistStore()
const rootStore = useRootStore()

const filled = computed(() => wishlistStore.itemFavorited(props.product.id))

const { breakpoints } = useBreakpoints()
const isMobile = computed(() => {
  return isMounted.value && breakpoints.xs.value
})

const contentClass = computed(() => {
  if (!isMobile.value && props.categoryPage) {
    return 'wishlist-menu'
  } else {
    return ''
  }
})

const attach = computed(() => {
  if (isMobile.value || !props.categoryPage) {
    return false
  } else {
    return `#image-${props.product.id}`
  }
})
const isAttached = computed(() => {
  return !!attach.value
})
const menuHeight = computed(() => {
  return isAttached.value ? '100%' : '275px'
})
const menuWidth = computed(() => {
  return isAttached.value ? '100%' : '340px'
})

const triggerLogin = () => {
  rootStore.authMessage = 'wishlist'
  authStore.updateAuthMenu(true)
}
const closeNewWishlist = () => {
  newWishlist.value = false
  showLists.value = true
}
const closeMenu = () => {
  newWishlist.value = false
  showLists.value = false
}

const FOCUS_RETRY_FRAMES = 15

const nextFrame = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()))

/** Bumped by every focus request so a retry loop still waiting for an older one gives up. */
let focusRun = 0

const focusInto = async (getMenu: () => { contentEl?: HTMLElement } | null) => {
  const run = ++focusRun
  await nextTick()
  for (let frame = 0; frame < FOCUS_RETRY_FRAMES; frame++) {
    await nextFrame()
    if (run !== focusRun) {
      return
    }
    const root = getMenu()?.contentEl
    const marked = root?.querySelector<HTMLElement>('[data-focus-first]')
    const target = marked?.matches(FOCUSABLE)
      ? marked
      : (marked?.querySelector<HTMLElement>(FOCUSABLE) ?? root?.querySelector<HTMLElement>(FOCUSABLE))
    if (!target?.isConnected) {
      continue
    }
    target.focus()
    if (document.activeElement === target) {
      return
    }
  }
}

watch(newWishlist, (open) => {
  if (open) {
    focusInto(() => newWishlistMenuRef.value)
  } else if (showLists.value) {
    focusInto(() => listMenuRef.value)
  } else if (newWishlistMenuRef.value?.contentEl?.contains(document.activeElement)) {
    // The create menu has no activator, so VMenu cannot return focus to the heart on its own.
    focusRun++
    wishlistButtonRef.value?.$el.focus({ preventScroll: true })
  }
})

const { loading, processAction } = useAction()

const { canUseWishlist } = useWishlist()

const openLists = ($event: Event, click: EventListener) => {
  processAction(async () => {
    if (canUseWishlist.value) {
      click($event)
      await Promise.all([
        wishlistStore.fetchLists(),
        wishlistStore.fetchListsWithItem(props.product.id)
      ])
      await focusInto(() => listMenuRef.value)
    } else if (breakpoints.smAndDown.value) {
      if (route.name !== 'auth-sign-in') {
        rootStore.authMessage = 'wishlist'
      }
      await router.push({ name: 'auth-sign-in', query: { redirectOnSuccess: route.path } })
    } else {
      triggerLogin()
    }
  })
}

onMounted(() => {
  isMounted.value = true
})
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

// only used when attached
.wishlist-menu {
  top: 48px !important;
  left: 0 !important;
  right: 0 !important;
  cursor: auto;
}
.wishlist-button {
  // Circle: 32 x 32, color #f8f8f8
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgb(var(--v-theme-backgroundDiv));
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0; // Remove any default padding

  // Outline heart (not selected): width 16px, border color #333 (--v-primary-base)
  :deep(.v-icon__svg) {
    width: 16px;
    height: 16px;
    color: rgb(var(--v-theme-primary));
    transition: color 0.2s ease;
  }

  // Filled heart (selected): width 16px, color #e33c84 (--v-secondary-base)
  &.selected {
    :deep(.v-icon__svg) {
      color: rgb(var(--v-theme-secondary));
      fill: rgb(var(--v-theme-secondary));
    }
  }

  &:hover {
    background-color: #f0f0f0;
  }

  &:focus {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }
}

.wishlist-text-button {
  text-decoration: underline;
  text-transform: none;
}
</style>
